import type { BridgeContext, BridgeFeatures, BridgeInvokeHandler } from './types';
import type { TBridgeMiddleware } from './methods/types';
import { bridgeMethods } from './methods';
import { withMetrics } from './middleware/withMetrics';

type AnyBridgeMethod = (context: BridgeContext, payload: unknown) => Promise<unknown>;

/**
 * Middlewares applied to every bridge method across all runtimes.
 * Order is outermost-first: the first entry wraps all subsequent ones.
 */
const DEFAULT_MIDDLEWARES: TBridgeMiddleware[] = [withMetrics];

const applyMiddleware = (
	method: AnyBridgeMethod,
	methodName: string,
	middlewares: TBridgeMiddleware[],
): AnyBridgeMethod => middlewares.reduce((m, mw) => mw(m, methodName), method);

/**
 * Builds the `BridgeFeatures` map expected by `createWorkerBridge` from the
 * registered `bridgeMethods`.
 *
 * Each method is wrapped so that it receives the `data` field from the bridge
 * message payload, and returns `true` as a default acknowledgement when the
 * method returns `undefined`.
 *
 * Product-provided callbacks (e.g. `openModal`, `showFlag`) are accessed via
 * `getBridgeContext().bridgeConfig` at invocation time rather than being passed
 * in directly, so the bridge features map never needs to be rebuilt when the
 * product's bridge prop changes.
 *
 * @param extraMiddlewares - Optional runtime-specific middlewares appended after the defaults.
 *   Applied left-to-right: the first middleware in the array is the outermost wrapper.
 */
export const createBridgeFeatures = (
	getBridgeContext: () => BridgeContext,
	extraMiddlewares: TBridgeMiddleware[] = [],
): BridgeFeatures => {
	const middlewares = [...DEFAULT_MIDDLEWARES, ...extraMiddlewares];
	return Object.entries(bridgeMethods).reduce((features, [methodName, method]) => {
		const wrapped = applyMiddleware(method as AnyBridgeMethod, methodName, middlewares);
		features[methodName] = convertToBridgeInvokeHandler((payload: unknown) => {
			// Call getBridgeContext() at invocation time, not creation time, so each
			// bridge call always uses the latest context rather than a stale snapshot.
			return wrapped(getBridgeContext(), payload);
		});
		return features;
	}, {} as BridgeFeatures);
};

const convertToBridgeInvokeHandler = (
	method?: (data: unknown) => Promise<unknown>,
): BridgeInvokeHandler => {
	return async ({ data }) => {
		if (method) {
			return (await method(data)) ?? true;
		}
		return false;
	};
};
